import { Link } from '@inertiajs/react';
import { Check, Clock, ShieldCheck } from 'lucide-react';

import { Btn } from '@/Components/Btn';
import { Headshot } from '@/Components/Headshot';
import { PhoneCTA } from '@/Components/PhoneCTA';
import { Pill } from '@/Components/Pill';
import { Stars } from '@/Components/Stars';
import type { LawyerCardData } from '@/Types/lawyer';

/**
 * Horizontal lawyer card. Used on search results, state hub lists, "lawyers
 * for X practice area" pages. Three columns at md+: headshot + verified pill /
 * name+meta+bio+areas / Stars+response+PhoneCTA+ViewProfile. Stacks under md.
 *
 *   <LawyerCardH data={lawyer} />
 */

export interface LawyerCardHProps {
    data: LawyerCardData;
    className?: string;
    profileHref?: string;
}

export function LawyerCardH({ data, className, profileHref }: LawyerCardHProps) {
    const cls = [
        'bg-card border border-rule rounded-[14px] p-5',
        'grid gap-6 grid-cols-1 md:grid-cols-[112px_1fr_240px]',
        className ?? '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <article className={cls} aria-label={`${data.name} — ${data.firm}`}>
            <div className="flex flex-col gap-2 items-center">
                <Headshot
                    initials={data.initials}
                    hue={data.hue}
                    size={96}
                    radius={10}
                />
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium tracking-wide uppercase rounded-full bg-sage-tint text-sage">
                    <Check size={10} aria-hidden="true" />
                    Verified
                </span>
            </div>
            <div className="flex flex-col gap-2.5 min-w-0">
                <div>
                    <div className="flex items-baseline gap-2.5 flex-wrap">
                        <h3 className="text-[22px] leading-tight">
                            {data.name}
                        </h3>
                        <span className="text-[12px] text-ink-mute">
                            {data.years} yrs licensed
                        </span>
                    </div>
                    <div className="text-[13px] text-ink-soft mt-0.5">
                        {data.firm} · {data.city}, {data.state}
                        {data.distance !== undefined &&
                            ` · ${data.distance} mi`}
                    </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                    {data.areas.slice(0, 4).map((a) => (
                        <Pill key={a} tone="muted">
                            {a}
                        </Pill>
                    ))}
                </div>
                {data.bio !== undefined && (
                    <p className="text-[14px] text-ink-soft leading-relaxed max-w-xl">
                        {data.bio}
                    </p>
                )}
                <div className="flex flex-wrap gap-3 text-[12px] text-ink-mute items-center">
                    <span className="inline-flex items-center gap-1.5 text-sage">
                        <ShieldCheck size={14} aria-hidden="true" />
                        State Bar of {data.state}
                    </span>
                    <span aria-hidden="true">·</span>
                    <span>Free consultation</span>
                    {data.langs?.includes('ES') === true && (
                        <>
                            <span aria-hidden="true">·</span>
                            <span>Habla español</span>
                        </>
                    )}
                    {data.contingency === true && (
                        <>
                            <span aria-hidden="true">·</span>
                            <span>Contingency only</span>
                        </>
                    )}
                </div>
            </div>
            <div className="flex flex-col gap-2.5 items-stretch md:border-l md:border-rule-soft md:pl-6">
                <div className="flex flex-col gap-1">
                    <Stars value={data.rating} reviews={data.reviews} />
                    {data.responseTime !== undefined && (
                        <div className="text-[12px] text-ink-mute inline-flex items-center gap-1.5">
                            <Clock size={12} aria-hidden="true" />
                            <span>Responds in ~{data.responseTime}</span>
                        </div>
                    )}
                </div>
                <PhoneCTA
                    number={data.phone}
                    kind="secondary"
                    size="sm"
                    full
                />
                {profileHref ? (
                    <Link
                        href={profileHref}
                        aria-label={`View ${data.name}'s profile`}
                        className="inline-flex h-11 w-full items-center justify-center rounded-md bg-ember px-4.5 text-[15px] font-medium tracking-tight text-paper transition-colors hover:bg-ember-deep active:bg-ember-deep"
                    >
                        View profile
                    </Link>
                ) : (
                    <Btn kind="primary" full>
                        View profile
                    </Btn>
                )}
            </div>
        </article>
    );
}

export default LawyerCardH;
